import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { RiDragMove2Fill, RiDeleteBin6Line } from "react-icons/ri";
import { Zap } from "lucide-react";

/**
 * SequenceEditor — drag-reorderable playback schedule.
 *
 * Props:
 *   sequence       [{sequenceId, mediaId}]
 *   media          [{mediaId, fileName, type, campaignId, campaignName, advertiserName, duration}]
 *   isEditing      boolean
 *   onSequenceChange (newSequence) => void
 *   title          string  e.g. "Playback Sequence"
 */
const SequenceEditor = ({
  sequence = [],
  media = [],
  isEditing = false,
  onSequenceChange,
  title = "Playback Sequence",
}) => {
  // Build a quick lookup: mediaId -> media item
  const mediaMap = {};
  for (const m of media) {
    if (m.mediaId != null) mediaMap[m.mediaId] = m;
  }

  // ---------- drag-reorder ----------
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(sequence);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    // re-number sequenceIds 1-based
    const renumbered = items.map((s, i) => ({
      ...s,
      sequenceId: i + 1,
    }));
    onSequenceChange(renumbered);
  };

  // ---------- delete ----------
  const handleDelete = (index) => {
    const items = sequence.filter((_, i) => i !== index);
    const renumbered = items.map((s, i) => ({
      ...s,
      sequenceId: i + 1,
    }));
    onSequenceChange(renumbered);
  };

  // ---------- Generate Sequence (proportional fair-share interleaving) ----------
  const generateSequence = () => {
    // 1. Group media by campaignId
    const buckets = {};
    const campaignOrder = [];
    for (const m of media) {
      const cid = m.campaignId || "__none__";
      if (!buckets[cid]) {
        buckets[cid] = [];
        campaignOrder.push(cid);
      }
      buckets[cid].push(m);
    }

    if (campaignOrder.length === 0) return;

    // 2. Sequence length = max(bucket.length) × totalCampaigns
    const maxLen = Math.max(...campaignOrder.map((c) => buckets[c].length));
    const totalCampaigns = campaignOrder.length;
    const seqLength = maxLen * totalCampaigns;

    // 3. Build interleaved sequence
    const newSequence = [];
    // Track per-campaign index
    const campaignIdx = {};
    for (const c of campaignOrder) campaignIdx[c] = 0;

    for (let slot = 0; slot < seqLength; slot++) {
      const cid = campaignOrder[slot % totalCampaigns];
      const bucket = buckets[cid];
      const mIdx = campaignIdx[cid] % bucket.length;
      campaignIdx[cid]++;

      newSequence.push({
        sequenceId: slot + 1,
        mediaId: bucket[mIdx].mediaId,
      });
    }

    onSequenceChange(newSequence);
  };

  // ---------- stats ----------
  const campaignSet = new Set();
  let totalDuration = 0;
  for (const s of sequence) {
    const m = mediaMap[s.mediaId];
    if (m) {
      if (m.campaignId) campaignSet.add(m.campaignId);
      totalDuration += m.duration || 0;
    }
  }
  const durationMin = Math.floor(totalDuration / 60);
  const durationSec = totalDuration % 60;

  return (
    <div className="mt-6 rounded-lg border border-cyan-100/25 bg-slate-950/20 p-4 shadow-xl shadow-blue-950/10 backdrop-blur md:p-5">
      {/* HEADER */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase text-cyan-100/80">
            Ordered Schedule
          </p>
          <h2 className="m-0 text-xl font-black text-white">{title}</h2>
          {sequence.length > 0 && (
            <p className="mt-1 text-xs text-cyan-100/50">
              {sequence.length} items · {campaignSet.size} campaign
              {campaignSet.size !== 1 ? "s" : ""} ·{" "}
              {durationMin > 0 ? `~${durationMin}m ${durationSec}s` : `${durationSec}s`}{" "}
              total
            </p>
          )}
        </div>

        {isEditing && (
          <button
            onClick={generateSequence}
            disabled={media.length === 0}
            className={`inline-flex min-h-10 items-center gap-2 rounded-lg px-4 py-2 text-sm font-black shadow transition ${
              media.length === 0
                ? "cursor-not-allowed bg-white/20 text-white/40"
                : "bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:from-amber-500 hover:to-orange-600"
            }`}
          >
            <Zap size={16} />
            Generate Sequence
          </button>
        )}
      </div>

      {/* EMPTY STATE */}
      {sequence.length === 0 && (
        <p className="text-sm text-cyan-100/60 italic">
          No sequence configured yet.{" "}
          {isEditing && media.length > 0 && (
            <span>
              Click <strong>Generate Sequence</strong> to auto-create one.
            </span>
          )}
        </p>
      )}

      {/* SEQUENCE LIST */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sequence-list">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-1.5"
            >
              {sequence.map((s, index) => {
                const m = mediaMap[s.mediaId];
                return (
                  <Draggable
                    key={`seq-${index}`}
                    draggableId={`seq-${index}`}
                    index={index}
                    isDragDisabled={!isEditing}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition ${
                          snapshot.isDragging
                            ? "border-sky-400 bg-sky-950/60 shadow-lg"
                            : "border-cyan-100/20 bg-white/5 hover:bg-white/10"
                        }`}
                      >
                        {/* drag handle */}
                        <div
                          {...provided.dragHandleProps}
                          className={`shrink-0 ${
                            isEditing
                              ? "cursor-grab text-cyan-100/60 hover:text-cyan-100"
                              : "text-cyan-100/20"
                          }`}
                        >
                          <RiDragMove2Fill size={14} />
                        </div>

                        {/* sequence number */}
                        <span className="shrink-0 w-8 text-center rounded-full bg-white/10 px-1.5 py-0.5 text-xs font-black text-cyan-200">
                          #{s.sequenceId}
                        </span>

                        {/* media info */}
                        <div className="min-w-0 flex-1 flex items-center gap-2 overflow-hidden">
                          <span className="truncate text-sm font-semibold text-white">
                            {m?.fileName || `media #${s.mediaId}`}
                          </span>
                        </div>

                        {/* campaign badge */}
                        {m?.campaignName && (
                          <span className="hidden sm:inline-flex shrink-0 rounded-full bg-amber-900/50 px-2 py-0.5 text-[10px] font-black text-amber-200">
                            {m.campaignName}
                          </span>
                        )}

                        {/* type badge */}
                        {m?.type && (
                          <span className="shrink-0 rounded-full bg-sky-900/50 px-2 py-0.5 text-[10px] font-black uppercase text-sky-300">
                            {m.type}
                          </span>
                        )}

                        {/* delete */}
                        {isEditing && (
                          <button
                            onClick={() => handleDelete(index)}
                            className="shrink-0 text-red-400/60 hover:text-red-400 transition"
                          >
                            <RiDeleteBin6Line size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default SequenceEditor;
